import { ChangeDetectionStrategy, Component, signal, OnDestroy } from '@angular/core';
import { LayoutService } from '@layout/core/services';
import { CurrentUserService } from '@core/services';
import { CurrentUser, UserAccount } from '@shared/interfaces';
import moment from 'moment';
import { ToolbarModule } from 'primeng/toolbar';
import { TranslateModule } from '@ngx-translate/core';
import { Router, RouterLink, NavigationEnd } from '@angular/router';
import { ToolbarComponent } from '@layout/components/toolbar/toolbar.component';
import { NgOptimizedImage } from '@angular/common';
import { DecoIconComponent } from '@shared/components/deco-icon/deco-icon.component';
import { HeaderMixpanelService } from '@shared/mixpanel';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: true,
    imports: [ToolbarModule, TranslateModule, RouterLink, ToolbarComponent, NgOptimizedImage, DecoIconComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnDestroy {
    isSidebarCollapsed = signal<boolean>(true);

    currentUser = signal<CurrentUser | null>(this.currentUserService.getCurrentUser());
    impersonatedUser = signal<UserAccount | null>(this.currentUserService.getImpersonatedUser());

    welcomeMessage = signal<string>(this.computeWelcomeMessage());

    shouldHideUserInfo = signal<boolean>(false);

    private subscriptions = new Subscription();
    private popStateHandler?: () => void;

    constructor(
        private layoutService: LayoutService,
        private currentUserService: CurrentUserService,
        private mixpanel: HeaderMixpanelService,
        private router: Router
    ) {
        this.updateShouldHideUserInfo();

        this.layoutService.isSidebarCollapsed$.subscribe(isCollapsed => {
            this.isSidebarCollapsed.set(isCollapsed);
        });

        this.currentUserService.currentUser$.subscribe(user => {
            this.currentUser.set(user);
        });

        this.currentUserService.impersonatedUser$.subscribe(user => {
            this.impersonatedUser.set(user);
        });

        this.subscriptions.add(
            this.router.events
                .pipe(filter(event => event instanceof NavigationEnd))
                .subscribe((event: NavigationEnd) => {
                    this.updateShouldHideUserInfo(event.urlAfterRedirects);
                })
        );

        this.popStateHandler = () => {
            // ensure router.url is updated after popstate
            setTimeout(() => {
                this.updateShouldHideUserInfo();
            }, 0);
        };
        window.addEventListener('popstate', this.popStateHandler);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        if (this.popStateHandler) {
            window.removeEventListener('popstate', this.popStateHandler);
        }
    }

    private updateShouldHideUserInfo(url?: string): void {
        const routeUrl = url || this.router.url;
        const shouldHide = routeUrl.includes('order-results-redirect') || routeUrl.includes('order-details-shared');
        this.shouldHideUserInfo.set(shouldHide);
    }

    stopImpersonation(): void {
        this.mixpanel.logStopImpersonation();
        this.currentUserService.removeUserToImpersonate();
    }

    private computeWelcomeMessage(): string {
        const now = moment();
        const startOfDay = now.clone().startOf('day');
        const endOfMorning = startOfDay.clone().hour(11).minute(59).second(59);
        const endOfAfternoon = startOfDay.clone().hour(15).minute(59).second(59);
        const endOfEvening = startOfDay.clone().hour(23).minute(59).second(59);

        if (now.isBetween(startOfDay, endOfMorning, null, '[]')) {
            return 'header.goodMorning';
        }
        if (now.isBetween(endOfMorning, endOfAfternoon, null, '[]')) {
            return 'header.goodAfternoon';
        }
        if (now.isBetween(endOfAfternoon, endOfEvening, null, '[]')) {
            return 'header.goodEvening';
        }

        return 'header.welcome';
    }
}
